/**
 * A rota `/integracoes` (história 11.2, FR-82) — WhatsApp, Carga Máquina e E-mail, em três abas
 * (protótipo v2, realinhamento de 28/09/2026).
 *
 * **É tela de todos os perfis** (`acessar_integracoes`, concedida nos quatro de fábrica): é aqui que
 * cada um conecta o **próprio** número (FR-71) — o cartão saiu de `/whatsapp`, que ficou só com as
 * conversas, e não foi duplicado: duas telas operando a mesma instância teriam dois pontos de
 * verdade para "conectado". As credenciais só existem na tela para quem tem
 * `editar_credenciais_de_integracao`, e quem recorta de verdade é o serviço (`credenciais: null` e
 * `403`). O detalhe de quem vê o quê está em `modulos/integracoes/CartoesDeIntegracao.tsx`.
 *
 * **A aba vive na URL** (`?aba=whatsapp|erp|email`), no molde de `/configuracao`: `validateSearch`
 * descarta o que não é uma das três, e a barra é um `tablist` com as setas e o `tabIndex`
 * itinerante do padrão ARIA. **A barra só mostra as abas que têm algo para esta sessão**: Carga
 * Máquina para quem edita credenciais ou lê os recebimentos (gestão), E-mail só para quem edita
 * credenciais. Esconder é conveniência — a aba digitada na URL por quem não a vê cai em WhatsApp,
 * e o serviço continua recusando o que não é dele.
 *
 * **O título é publicado pela rota**, como nas outras telas com item de menu, para o `<h1>` não
 * depender do item.
 */

import { createRoute } from '@tanstack/react-router'

import type { Sessao } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import {
  AbaDeEmail,
  AbaDoCargaMaquina,
  AbaDoWhatsapp,
  PERMISSAO_DE_CREDENCIAIS,
  temAbaDoCargaMaquina,
} from '@/modulos/integracoes/CartoesDeIntegracao'
import { useDefinirTitulo } from '@/shell/Shell'
import { CAMINHOS, TITULO_DAS_INTEGRACOES } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'
import '@/modulos/integracoes/integracoes.css'

/** As abas da tela, na ordem da barra. */
export type AbaDasIntegracoes = 'whatsapp' | 'erp' | 'email'

export const ABAS_DAS_INTEGRACOES: readonly { aba: AbaDasIntegracoes; rotulo: string }[] = [
  { aba: 'whatsapp', rotulo: 'WhatsApp' },
  { aba: 'erp', rotulo: 'Carga Máquina (ERP)' },
  { aba: 'email', rotulo: 'E-mail' },
]

/** A aba que `/integracoes` cru abre. */
const ABA_PADRAO: AbaDasIntegracoes = 'whatsapp'

/** O nome acessível da barra de abas. */
export const NOME_DA_BARRA = 'Seções das Integrações'

/** As abas que a barra mostra para esta sessão. */
export function abasVisiveis(
  sessao: Pick<Sessao, 'permissoes' | 'escopo_de_carteira'> | undefined,
): readonly { aba: AbaDasIntegracoes; rotulo: string }[] {
  const editaCredenciais = sessao !== undefined && PERMISSAO_DE_CREDENCIAIS in sessao.permissoes
  return ABAS_DAS_INTEGRACOES.filter(
    (item) =>
      item.aba === 'whatsapp' ||
      (item.aba === 'erp' && temAbaDoCargaMaquina(sessao)) ||
      (item.aba === 'email' && editaCredenciais),
  )
}

/** O que a URL desta tela carrega. */
export interface BuscaDasIntegracoes {
  aba?: AbaDasIntegracoes
}

const ID_DO_PAINEL = 'integracoes-painel'

function idDaAba(aba: AbaDasIntegracoes): string {
  return `integracoes-aba-${aba}`
}

/** A aba que uma tecla da barra abre, ou `null` quando a tecla não é da barra (padrão ARIA). */
function abaDaTecla(
  tecla: string,
  posicao: number,
  abas: readonly { aba: AbaDasIntegracoes }[],
): AbaDasIntegracoes | null {
  const total = abas.length
  const indice =
    tecla === 'ArrowRight'
      ? (posicao + 1) % total
      : tecla === 'ArrowLeft'
        ? (posicao - 1 + total) % total
        : tecla === 'Home'
          ? 0
          : tecla === 'End'
            ? total - 1
            : null
  return indice === null ? null : (abas[indice]?.aba ?? null)
}

function ConteudoDaAba({ aba }: { aba: AbaDasIntegracoes }) {
  switch (aba) {
    case 'whatsapp':
      return <AbaDoWhatsapp />
    case 'erp':
      return <AbaDoCargaMaquina />
    case 'email':
      return <AbaDeEmail />
  }
}

function TelaDeIntegracoes() {
  useDefinirTitulo(TITULO_DAS_INTEGRACOES)
  // A anotação é explícita pela mesma razão das outras telas com busca: `useSearch` da própria
  // rota vê o tipo em construção.
  const { aba }: BuscaDasIntegracoes = rotaDeIntegracoes.useSearch()
  const navegar = rotaDeIntegracoes.useNavigate()
  // Conferida de novo aqui, e não só no `validateSearch`: a sessão é quem diz que abas existem, e
  // a aba válida que esta sessão não vê cai na padrão.
  const { data: sessao } = useSessao()
  const abas = abasVisiveis(sessao)
  const aberta: AbaDasIntegracoes = abas.find((item) => item.aba === aba)?.aba ?? ABA_PADRAO

  function abrir(destino: AbaDasIntegracoes) {
    void navegar({ search: destino === ABA_PADRAO ? {} : { aba: destino } })
  }

  return (
    <div className="integracoes">
      <div className="integracoes__abas" role="tablist" aria-label={NOME_DA_BARRA}>
        {abas.map((item, posicao) => {
          const ativa = item.aba === aberta
          return (
            <button
              key={item.aba}
              id={idDaAba(item.aba)}
              type="button"
              role="tab"
              className="integracoes__aba"
              aria-selected={ativa}
              aria-controls={ID_DO_PAINEL}
              tabIndex={ativa ? 0 : -1}
              onClick={() => {
                abrir(item.aba)
              }}
              onKeyDown={(evento) => {
                const destino = abaDaTecla(evento.key, posicao, abas)
                if (destino === null) return
                evento.preventDefault()
                abrir(destino)
                document.getElementById(idDaAba(destino))?.focus()
              }}
            >
              {item.rotulo}
            </button>
          )
        })}
      </div>
      <div role="tabpanel" id={ID_DO_PAINEL} aria-labelledby={idDaAba(aberta)}>
        <ConteudoDaAba aba={aberta} />
      </div>
    </div>
  )
}

export const rotaDeIntegracoes = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.integracoes,
  component: TelaDeIntegracoes,
  /** A URL é entrada: o que não for uma das três abas cai fora aqui, e a tela abre em WhatsApp. */
  validateSearch: (parametros: Record<string, unknown>): BuscaDasIntegracoes => {
    const aba = ABAS_DAS_INTEGRACOES.find((item) => item.aba === parametros.aba)?.aba
    return aba === undefined || aba === ABA_PADRAO ? {} : { aba }
  },
})
